import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services'
import { queryKeys, invalidateQueries } from '@/lib/query'
import type { Building, ComplianceTarget } from '@/types'

interface CreateBuildingData {
  name: string
  address?: string
  gps_radius_metres?: number
  latitude?: number
  longitude?: number
  gps_center_latitude?: number
  gps_center_longitude?: number
}

interface UpdateBuildingData {
  name?: string
  address?: string
  gps_radius_metres?: number
  latitude?: number
  longitude?: number
  gps_center_latitude?: number
  gps_center_longitude?: number
  /**
   * Compliance target override for this building.
   * - omitted (undefined) → don't touch the stored target
   * - null               → clear the stored target (inherit from org)
   * - object             → set / replace the stored target
   */
  default_target?: ComplianceTarget | null
}

/**
 * Hook to fetch all buildings for an organization.
 */
export function useBuildings(orgId: string) {
  return useQuery({
    queryKey: queryKeys.buildings.all(orgId),
    queryFn: () => api.get<Building[]>('/buildings'),
    enabled: !!orgId,
  })
}

/**
 * Hook to fetch a single building by ID.
 */
export function useBuilding(buildingId: string) {
  return useQuery({
    queryKey: queryKeys.buildings.detail(buildingId),
    queryFn: () => api.get<Building>(`/buildings/${buildingId}`),
    enabled: !!buildingId,
  })
}

/**
 * Hook to create a new building.
 */
export function useCreateBuilding(orgId: string) {
  return useMutation({
    mutationFn: (data: CreateBuildingData) => api.post<Building>('/buildings', data),
    onSuccess: () => {
      invalidateQueries.buildings(orgId)
    },
  })
}

/**
 * Hook to update a building.
 */
export function useUpdateBuilding(buildingId: string, orgId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateBuildingData) => api.put<Building>(`/buildings/${buildingId}`, data),
    onSuccess: (updatedBuilding) => {
      // Update the cache directly for this building
      queryClient.setQueryData(queryKeys.buildings.detail(buildingId), updatedBuilding)
      // Invalidate the list
      invalidateQueries.buildings(orgId)
    },
  })
}

/**
 * Hook to delete a building.
 */
export function useDeleteBuilding(orgId: string) {
  return useMutation({
    mutationFn: (buildingId: string) => api.delete<void>(`/buildings/${buildingId}`),
    onSuccess: () => {
      invalidateQueries.buildings(orgId)
    },
  })
}
